import { defineStore } from 'pinia';
import { ref } from 'vue';

import { i18n as i18nSetup } from '#ui-shared/i18n/i18n';

interface TextPromptModalProps {
  title?: string;
  textFieldLabel?: string;
  placeholder?: string;
  initialValue?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  maxCharacters?: number;
  requireNonEmptyValue?: boolean;
  nonEmptyValueErrorMessage?: string;
  validateValue?: (value: string) => boolean;
  validationErrorMessage?: string;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}

export const useTextPromptModalStore = defineStore('textPromptModalStore', () => {
  const { t } = i18nSetup.global;

  const isOpen = ref(false);
  const title = ref<string>('');
  const textFieldLabel = ref<string>('');
  const placeholder = ref<string>('');
  const initialValue = ref<string>('');
  const confirmButtonLabel = ref<string>(t('common.confirm'));
  const cancelButtonLabel = ref<string>(t('common.cancel'));
  const maxCharacters = ref<number | undefined>(undefined);
  const requireNonEmptyValue = ref(false);
  const nonEmptyValueErrorMessage = ref<string | undefined>(undefined);
  const validateValue = ref<((value: string) => boolean) | undefined>(undefined);
  const validationErrorMessage = ref<string | undefined>(undefined);
  const defaultOnConfirm = (): void => {
    isOpen.value = false;
  };
  const onConfirm = ref<(value: string) => Promise<void> | void>(defaultOnConfirm);
  const onCancel = ref<(() => void) | undefined>(undefined);

  const reset = (): void => {
    if (onCancel.value) {
      onCancel.value();
    }

    isOpen.value = false;
    title.value = '';
    textFieldLabel.value = '';
    placeholder.value = '';
    initialValue.value = '';
    confirmButtonLabel.value = t('common.confirm');
    cancelButtonLabel.value = t('common.cancel');
    maxCharacters.value = undefined;
    requireNonEmptyValue.value = false;
    nonEmptyValueErrorMessage.value = undefined;
    validateValue.value = undefined;
    validationErrorMessage.value = undefined;
    onConfirm.value = defaultOnConfirm;
    onCancel.value = undefined;
  };

  const closeModal = (): void => {
    isOpen.value = false;
    reset();
  };

  const handleConfirm = async (value: string): Promise<void> => {
    if (onConfirm.value) {
      await onConfirm.value(value);
    }

    closeModal();
  };

  const handleCancel = (): void => {
    if (onCancel.value) {
      onCancel.value();
    }

    closeModal();
  };

  function showTextPromptModal(options: TextPromptModalProps): void {
    isOpen.value = true;
    title.value = options.title || '';
    textFieldLabel.value = options.textFieldLabel || '';
    placeholder.value = options.placeholder || '';
    initialValue.value = options.initialValue || '';
    confirmButtonLabel.value = options.confirmButtonLabel || t('common.confirm');
    cancelButtonLabel.value = options.cancelButtonLabel || t('common.cancel');
    maxCharacters.value = options.maxCharacters;
    requireNonEmptyValue.value = options.requireNonEmptyValue ?? false;
    nonEmptyValueErrorMessage.value = options.nonEmptyValueErrorMessage;
    validateValue.value = options.validateValue;
    validationErrorMessage.value = options.validationErrorMessage;
    onConfirm.value = options.onConfirm;
    onCancel.value = options.onCancel;
  }

  return {
    showTextPromptModal,
    closeModal,
    handleConfirm,
    handleCancel,
    isOpen,
    title,
    textFieldLabel,
    placeholder,
    initialValue,
    confirmButtonLabel,
    cancelButtonLabel,
    maxCharacters,
    requireNonEmptyValue,
    nonEmptyValueErrorMessage,
    validateValue,
    validationErrorMessage,
  };
});
